import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEntityAuditLogs } from '@/hooks/queries/useFinanceAuditLogs';
import { AuditDiff } from './AuditDiff';
import { getActionLabel } from './auditActionLabels';

interface AuditLogPanelProps {
  entityType: string;
  entityId: string;
  onClose: () => void;
}

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return isoString;
  }
}

export function AuditLogPanel({ entityType, entityId, onClose }: AuditLogPanelProps) {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading, error } = useEntityAuditLogs(entityType, entityId, {
    page,
    pageSize: 20,
  });

  const entries = (data as any)?.items || [];
  const totalCount = (data as any)?.totalCount || 0;
  const hasMore = (page - 1) * 20 + entries.length < totalCount;

  const handleLoadMore = () => {
    setPage((p) => p + 1);
  };

  const entityLabel = entityType.toLowerCase();

  return (
    <div className="fixed right-0 top-0 h-full w-96 border-l bg-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <h2 className="text-lg font-semibold capitalize">{entityLabel} history</h2>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="overflow-y-auto p-4" style={{ height: 'calc(100% - 60px)' }}>
        {isLoading && <div className="text-sm text-gray-500">Loading audit log...</div>}

        {error && (
          <div className="text-sm text-red-600">Failed to load audit log. Please try again.</div>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <div className="text-sm text-gray-500">No changes recorded.</div>
        )}

        {!isLoading && !error && entries.length > 0 && (
          <div className="space-y-4">
            {entries.map((entry: any) => (
              <div key={entry.id} className="border-b pb-4 last:border-b-0">
                {/* Timestamp and User */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">{formatTimestamp(entry.timestamp)}</div>
                  <div className="text-xs font-medium text-gray-700">{entry.userName}</div>
                </div>

                {/* Action and Details */}
                <div className="mt-1">
                  <div className="text-sm font-medium">
                    {getActionLabel(entry.action)}
                  </div>
                  {entry.details && (
                    <div className="mt-1 text-xs text-gray-600">{entry.details}</div>
                  )}
                </div>

                {/* Diff Toggle and Content */}
                {entry.oldValue || entry.newValue ? (
                  <div className="mt-2">
                    <button
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      {expandedId === entry.id ? 'Hide changes' : 'Show changes'}
                    </button>

                    {expandedId === entry.id && (
                      <AuditDiff oldValue={entry.oldValue} newValue={entry.newValue} />
                    )}
                  </div>
                ) : null}
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
